const en = {
  // Navbar
  nav: {
    services: "Services",
    about: "About",
    contact: "Contact",
    cta: "Get in Touch",
  },

  // Hero
  hero: {
    badge: "Cloud & AI Transformation Consultant",
    heading: "Your cloud, modernised.",
    headingAccent: "Your AI, governed.",
    description:
      "From cloud migration to Copilot governance, I help businesses leverage Microsoft 365 securely — turning AI agents into a value‑creating capability, not an uncontrolled experiment.",
    cta: "Book a Scoping Meeting",
    statAzure: "Azure & M365",
    statCopilot: "Copilot & Agents",
    statSecurity: "Security & Governance",
    statOkr: "OKR & Strategy",
  },

  // Services
  services: {
    label: "Services",
    heading: "Certified ≠ Secure. We prove the difference.",
    subheading: "Most organisations pass audits but fail incidents. We validate what actually works — not what looks good on paper.",
    flagship: {
      badge: "Flagship Assessment",
      title: "Compliance Reality Check",
      desc: "A structured assessment that tests whether your compliance controls actually function under pressure — not just whether they exist on paper. We expose the gap between audit artefacts and operational readiness.",
      cta: "Explore the Compliance Reality Check",
    },
    items: [
      {
        title: "Cloud Infrastructure",
        desc: "Azure & Microsoft 365 migration, architecture, and operational hardening — built for reality, not just reference designs.",
      },
      {
        title: "Copilot & AI Governance",
        desc: "Permissions audit, data boundaries, agent ownership, and auditability — because Copilot reveals what's already broken.",
      },
      {
        title: "Security & Compliance",
        desc: "Purview, Entra identity lifecycle, insider risk, and end-to-end security posture — validated, not assumed.",
      },
      {
        title: "Strategy & Board Advisory",
        desc: "OKR-aligned reporting, board-level AI governance, and executive advisory — from dashboards to decisions that matter.",
      },
    ],
  },

  // Quote
  quote: {
    text: "Copilot doesn't create risk\u00A0—",
    accent: "it reveals it.",
    body: "Most organisations already have overshared data, broken permissions, and compliance gaps. Copilot just makes them visible. The question isn't whether to adopt AI — it's whether you're ready for what it will surface.",
  },

  // Roadmap
  roadmap: {
    label: "AI Roadmap",
    heading: "Where Microsoft 365 AI is heading",
    subheading: "Three phases shaping enterprise AI — and where your organisation needs to be.",
    phases: [
      {
        phase: "Phase 1",
        period: "Now → 2026",
        title: "Copilot as the Productivity Layer",
        desc: "Microsoft 365 Copilot becomes the default interaction layer for documents, meetings, email, and search — grounded in Microsoft Graph and trimmed by permissions.",
        highlights: ["Context-aware assistance", "Multi-model AI", "Agent Mode for multi-step tasks"],
      },
      {
        phase: "Phase 2",
        period: "2025 → 2027",
        title: "AI Agents as Digital Coworkers",
        desc: "Autonomous and semi-autonomous agents that use tools, APIs, and Graph to execute goals — from triaging HR requests to preparing board decks.",
        highlights: ["Copilot Studio agents", "Declarative & custom agents", "Human-in-the-loop design"],
      },
      {
        phase: "Phase 3",
        period: "2026+",
        title: "Governed AI Operating Model",
        desc: "Enterprise-wide governance for AI identities, data boundaries, auditability, and compliance — ensuring agents are accountable, not uncontrolled.",
        highlights: ["Agent identity lifecycle", "AI usage policies", "Board-level reporting"],
      },
    ],
  },

  // About
  about: {
    label: "About",
    heading: "I exist because compliance theatre is dangerous",
    body: "I've spent years inside Microsoft ecosystems watching organisations tick boxes, pass audits, and remain fundamentally exposed. Certifications prove intent. They don't prove resilience. EastWest IT was built on a single conviction: the only compliance that matters is the kind that holds up when something goes wrong. I don't sell comfort — I validate whether your controls actually work, your AI is actually governed, and your board actually understands the risk picture.",
    highlights: [
      "Certified Azure & Microsoft 365 professional",
      "Mini-MBA in Leadership",
      "Advisory & board-level governance roles",
      "Outcome-driven — not artefact-driven",
    ],
  },

  // Contact
  contact: {
    label: "Get in Touch",
    heading: "Let's scope your next project",
    subheading: "Fill out the form below and I'll reach out to schedule a free scoping meeting.",
    nameLabel: "Name",
    namePlaceholder: "Your name",
    companyLabel: "Company",
    companyPlaceholder: "Your company",
    emailLabel: "Email",
    emailPlaceholder: "you@company.com",
    messageLabel: "How can I help?",
    messagePlaceholder: "Tell me about your project or challenge...",
    submit: "Send Message",
    submitting: "Sending...",
    successTitle: "Message sent!",
    successDesc: "Thank you — I'll get back to you shortly to schedule a scoping meeting.",
    errorTitle: "Something went wrong",
    errorDesc: "Please try again or email me directly.",
    emailDirect: "Or email me directly",
  },

  // Footer
  footer: {
    rights: "All rights reserved.",
  },
};

export type Translations = {
  nav: { services: string; about: string; contact: string; cta: string };
  hero: {
    badge: string; heading: string; headingAccent: string; description: string;
    cta: string; statAzure: string; statCopilot: string; statSecurity: string; statOkr: string;
  };
  services: {
    label: string; heading: string; subheading: string;
    flagship: { badge: string; title: string; desc: string; cta: string };
    items: { title: string; desc: string }[];
  };
  quote: { text: string; accent: string; body: string };
  roadmap: {
    label: string; heading: string; subheading: string;
    phases: { phase: string; period: string; title: string; desc: string; highlights: string[] }[];
  };
  about: { label: string; heading: string; body: string; highlights: string[] };
  contact: {
    label: string; heading: string; subheading: string;
    nameLabel: string; namePlaceholder: string; companyLabel: string; companyPlaceholder: string;
    emailLabel: string; emailPlaceholder: string; messageLabel: string; messagePlaceholder: string;
    submit: string; submitting: string;
    successTitle: string; successDesc: string; errorTitle: string; errorDesc: string; emailDirect: string;
  };
  footer: { rights: string };
};

export default en as Translations;
